import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  static values = {
    type: String,
    data: Object,
    partnerSlug: String,
    variant: String,
    timeBucket: String,
    dateSource: String
  }

  static targets = ["chartContainer", "viewToggle", "bucketToggle", "bucketSelect", "bucketLabel", "bucketDropdown", "viewSelect", "viewLabel", "viewDropdown", "dateSourceSelect", "dateSourceLabel", "dateSourceDropdown"]

  connect() {
    this.viewMode = "type" // "type" or "status"
    this.bucket = this.timeBucketValue || "week"
    this.dateSource = this.dateSourceValue || "imported"
    this.renderChart()

    // Close dropdowns on outside click
    this._outsideClickHandler = (e) => {
      if (this.hasBucketSelectTarget && !this.bucketSelectTarget.contains(e.target)) {
        this.bucketDropdownTarget.classList.add("hidden")
      }
      if (this.hasViewSelectTarget && !this.viewSelectTarget.contains(e.target)) {
        this.viewDropdownTarget.classList.add("hidden")
      }
      if (this.hasDateSourceSelectTarget && !this.dateSourceSelectTarget.contains(e.target)) {
        this.dateSourceDropdownTarget.classList.add("hidden")
      }
    }
    document.addEventListener("click", this._outsideClickHandler)
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
    if (this._outsideClickHandler) {
      document.removeEventListener("click", this._outsideClickHandler)
    }
  }

  renderChart() {
    if (this.variantValue === "claim_volume") {
      this.renderClaimVolumeChart()
    }
  }

  renderClaimVolumeChart() {
    const allData = this.dataValue
    if (!allData) return

    const sourceData = allData[this.dateSource] || allData["imported"] || {}
    const data = sourceData[this.bucket] || []
    if (data.length === 0) return

    const categories = data.map(d => d.period)
    const partnerSlug = this.partnerSlugValue

    let series, colors
    if (this.viewMode === "type") {
      series = [
        { name: "Professional", data: data.map(d => d.professional || 0) },
        { name: "Institutional", data: data.map(d => d.institutional || 0) }
      ]
      colors = ["#734CE0", "#E08C4C"]
    } else {
      series = [
        { name: "Closed — Prof", data: data.map(d => d.closed_prof || 0) },
        { name: "Closed — Inst", data: data.map(d => d.closed_inst || 0) },
        { name: "Open — Prof", data: data.map(d => d.open_prof || 0) },
        { name: "Open — Inst", data: data.map(d => d.open_inst || 0) }
      ]
      colors = ["#059669", "#34D399", "#D97706", "#FBBF24"]
    }

    const chartEl = this.hasChartContainerTarget ? this.chartContainerTarget : this.element

    const options = {
      chart: {
        type: "bar",
        stacked: true,
        height: 280,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false },
        events: {
          dataPointSelection: (_event, _chartContext, config) => {
            const period = categories[config.dataPointIndex]
            const seriesName = series[config.seriesIndex].name
            let params = `created_after=${period}`
            if (seriesName.includes("Professional") || seriesName.includes("Prof")) {
              params += "&claim_type=professional"
            } else if (seriesName.includes("Institutional") || seriesName.includes("Inst")) {
              params += "&claim_type=institutional"
            }
            if (seriesName.includes("Open")) {
              params += "&overall_status=in_progress"
            } else if (seriesName.includes("Closed")) {
              params += "&overall_status=complete"
            }
            window.location.href = `/partners/${partnerSlug}?${params}`
          }
        }
      },
      series: series,
      colors: colors,
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last",
          columnWidth: "60%"
        }
      },
      xaxis: {
        categories: categories,
        labels: {
          style: { colors: "#9CA3AF", fontSize: "11px" }
        },
        axisBorder: { color: "#E5E7EB" }
      },
      yaxis: {
        labels: {
          style: { colors: "#9CA3AF", fontSize: "11px" }
        }
      },
      grid: {
        borderColor: "#F0F1F3",
        strokeDashArray: 3
      },
      legend: {
        fontSize: "12px",
        labels: { colors: "#6B7280" }
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val) => val.toLocaleString()
        }
      },
      dataLabels: { enabled: false }
    }

    if (this.chart) {
      this.chart.destroy()
    }

    this.chart = new ApexCharts(chartEl, options)
    this.chart.render()
  }

  // Dropdown toggle methods for GROUP BY
  toggleBucketDropdown(event) {
    event.stopPropagation()
    this.bucketDropdownTarget.classList.toggle("hidden")
    if (this.hasViewDropdownTarget) {
      this.viewDropdownTarget.classList.add("hidden")
    }
    if (this.hasDateSourceDropdownTarget) {
      this.dateSourceDropdownTarget.classList.add("hidden")
    }
  }

  selectBucket(event) {
    const bucket = event.currentTarget.dataset.bucket
    if (bucket && bucket !== this.bucket) {
      this.bucket = bucket
      const titleMap = { day: "Day", week: "Week", month: "Month", quarter: "Quarter", year: "Year" }
      this.bucketLabelTarget.textContent = titleMap[bucket] || bucket

      this.bucketDropdownTarget.querySelectorAll("[data-bucket]").forEach(el => {
        el.classList.toggle("bg-support-light-200", el.dataset.bucket === bucket)
      })

      this.renderChart()
    }
    this.bucketDropdownTarget.classList.add("hidden")
  }

  // Dropdown toggle methods for DATE SOURCE
  toggleDateSourceDropdown(event) {
    event.stopPropagation()
    this.dateSourceDropdownTarget.classList.toggle("hidden")
    if (this.hasBucketDropdownTarget) {
      this.bucketDropdownTarget.classList.add("hidden")
    }
    if (this.hasViewDropdownTarget) {
      this.viewDropdownTarget.classList.add("hidden")
    }
  }

  selectDateSource(event) {
    const source = event.currentTarget.dataset.dateSource
    if (source && source !== this.dateSource) {
      this.dateSource = source
      const titleMap = { imported: "Imported", service_date: "Date of Service" }
      this.dateSourceLabelTarget.textContent = titleMap[source] || source

      this.dateSourceDropdownTarget.querySelectorAll("[data-date-source]").forEach(el => {
        el.classList.toggle("bg-support-light-200", el.dataset.dateSource === source)
      })

      this.renderChart()
    }
    this.dateSourceDropdownTarget.classList.add("hidden")
  }

  // Dropdown toggle methods for SHOW
  toggleViewDropdown(event) {
    event.stopPropagation()
    this.viewDropdownTarget.classList.toggle("hidden")
    if (this.hasBucketDropdownTarget) {
      this.bucketDropdownTarget.classList.add("hidden")
    }
    if (this.hasDateSourceDropdownTarget) {
      this.dateSourceDropdownTarget.classList.add("hidden")
    }
  }

  selectView(event) {
    const mode = event.currentTarget.dataset.viewMode
    if (mode && mode !== this.viewMode) {
      this.viewMode = mode
      const titleMap = { type: "Claim Type", status: "Claim Status" }
      this.viewLabelTarget.textContent = titleMap[mode] || mode

      this.viewDropdownTarget.querySelectorAll("[data-view-mode]").forEach(el => {
        el.classList.toggle("bg-support-light-200", el.dataset.viewMode === mode)
      })

      this.renderChart()
    }
    this.viewDropdownTarget.classList.add("hidden")
  }

  // Legacy toggle methods (kept for backward compatibility)
  toggleView(event) {
    const mode = event.currentTarget.dataset.viewMode
    if (mode && mode !== this.viewMode) {
      this.viewMode = mode
      if (this.hasViewToggleTarget) {
        this.updateToggleButtons(this.viewToggleTarget, mode)
      }
      this.renderChart()
    }
  }

  toggleBucket(event) {
    const bucket = event.currentTarget.dataset.bucket
    if (bucket && bucket !== this.bucket) {
      this.bucket = bucket
      if (this.hasBucketToggleTarget) {
        this.updateToggleButtons(this.bucketToggleTarget, bucket)
      }
      this.renderChart()
    }
  }

  updateToggleButtons(container, activeValue) {
    const activeClasses = "bg-core-purple-600 text-white border-none"
    const inactiveClasses = "bg-support-light-200 border border-support-dark-200 text-support-dark-400"

    container.querySelectorAll("button").forEach(btn => {
      const btnValue = btn.dataset.viewMode || btn.dataset.bucket
      btn.className = `rounded-md py-1 px-3 text-[11px] cursor-pointer font-semibold transition-all ${btnValue === activeValue ? activeClasses : inactiveClasses}`
    })
  }

  drillIn(event) {
    const url = event.currentTarget.dataset.drillUrl
    if (url) {
      window.location.href = url
    }
  }
}
